import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useScrumStore } from '@/lib/scrum-store';
import { DateInput } from '@/components/ui/date-input';
import { format } from 'date-fns';
import { useOwnerStore } from '@/lib/owner-store';
import { AISuggestionButton } from './ai-suggestion-button';

export function ScrumForm() {
  const { createUpdate } = useScrumStore();
  const { owners, fetchOwners } = useOwnerStore();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    yesterday: '',
    today: '',
    blockers: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    owner_id: ''
  });

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await createUpdate(formData);
      
      // Reset form
      setFormData(prev => ({
        ...prev,
        yesterday: '',
        today: '',
        blockers: '',
        owner_id: ''
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create scrum update');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-md">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-1">Owner</label>
        <select
          name="owner_id"
          value={formData.owner_id}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          required
        >
          <option value="">Select owner...</option>
          {owners.map((owner) => (
            <option key={owner.id} value={owner.id}>
              {owner.name} {owner.organization ? `(${owner.organization})` : ''}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Date</label>
        <DateInput
          name="date"
          value={formData.date}
          onChange={handleChange}
          max={format(new Date(), 'yyyy-MM-dd')}
          required
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">What tasks were completed yesterday?</label>
          <AISuggestionButton
            field="yesterday"
            content={formData.yesterday}
            onSuggestion={(suggestion) => setFormData(prev => ({ ...prev, yesterday: suggestion }))}
          />
        </div>
        <textarea
          name="yesterday"
          value={formData.yesterday}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          rows={3}
          required
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">What is the goal for today? </label>
          <AISuggestionButton
            field="today"
            content={formData.today}
            onSuggestion={(suggestion) => setFormData(prev => ({ ...prev, today: suggestion }))}
          />
        </div>
        <textarea
          name="today"
          value={formData.today}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          rows={3}
          required
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">What obstacles is the team facing?</label>
          <AISuggestionButton
            field="blockers"
            content={formData.blockers}
            onSuggestion={(suggestion) => setFormData(prev => ({ ...prev, blockers: suggestion }))}
          />
        </div>
        <textarea
          name="blockers"
          value={formData.blockers}
          onChange={handleChange}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          rows={3}
          placeholder=""
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full"
      >
        {isSubmitting ? 'Creating Update...' : 'Create Update'}
      </Button>
    </form>
  );
}